import React, { useContext, useEffect, useRef, useState } from "react";
import {
  View,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  Dimensions,
} from "react-native";
import Webcam from "react-webcam";
import Wrapper from "../../../components/wrapper";
import { markTaskAsCompleted } from "../../../utils/user";
import {
  getAssignedUrl,
  uploadFile,
  uploadVideoMetadata,
} from "../../../utils/video";
import IonIcons from "react-native-vector-icons/Ionicons";
import LoginContext from "../../../hooks/loginContext";
import CustomButton from "../../../components/button";
import { TranslateText } from "../../../components/translate-text";

const MIN_RECORD_TIME = 20; // time in seconds

const Task = ({ setIndex, task }) => {
  const webcamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [stoppedRecording, setStoppedRecording] = useState(false);
  const [questionVisible, setQuestionVisible] = useState(false);

  const [capturing, setCapturing] = useState(false);
  const [permissions, setPermissions] = useState({
    camera: false,
    microphone: false,
  });
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  // const [videoUrl, setVideoUrl] = useState(null);

  const isChrome = navigator?.userAgent.includes("Chrome");
  const { user } = useContext(LoginContext);

  const isMobile = {
    Android: function () {
      return navigator.userAgent.match(/Android/i);
    },
    BlackBerry: function () {
      return navigator.userAgent.match(/BlackBerry/i);
    },
    iOS: function () {
      return navigator.userAgent.match(/iPhone|iPad|iPod/i);
    },
    Opera: function () {
      return navigator.userAgent.match(/Opera Mini/i);
    },
    Windows: function () {
      return (
        navigator.userAgent.match(/IEMobile/i) ||
        navigator.userAgent.match(/WPDesktop/i)
      );
    },
    any: function () {
      return (
        isMobile.Android() ||
        isMobile.BlackBerry() ||
        isMobile.iOS() ||
        isMobile.Opera() ||
        isMobile.Windows()
      );
    },
  };

  const videoConstraints = {
    facingMode: "user",
    aspectRatio: isMobile.any() ? 16 / 9 : 9 / 16,
  };

  const getPermissions = async () => {
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
    } catch (e) {
      console.log(e);
    }
    if (stream) {
      setPermissions({
        camera: true,
        microphone: true,
      });
    } else {
      setPermissions({
        camera: false,
        microphone: false,
      });
    }
    if (mediaRecorderRef.current) {
      stopRecording();
    }
    return;
  };

  useEffect(() => {
    if (navigator) {
      getPermissions();
    }
  }, [navigator]);

  const handleDataAvailable = React.useCallback(
    ({ data }) => {
      if (data.size > 0) {
        setRecordedChunks((prev) => prev.concat(data));
        submitRecording(data);
      }
    },
    [setRecordedChunks]
  );

  const startRecording = () => {
    setCapturing(true);
    mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
      mimeType: isChrome ? "video/webm" : "video/mp4",
    });
    mediaRecorderRef.current.start();
    // TODO: autosubmit video after 3 minutes
    setTimeout(() => {
      stopRecording();
    }, 180000);
  };

  const stopRecording = () => {
    mediaRecorderRef.current.addEventListener(
      "dataavailable",
      handleDataAvailable
    );
    if (mediaRecorderRef.current.state == "recording") {
      mediaRecorderRef.current.stop();
      setStoppedRecording(true);
    }
  };

  const submitRecording = async (data) => {
    let userId = user.user_id;
    if (data) {
      const blob = data;
      var data = new Blob([blob], {
        type: isChrome ? "video/webm" : "video/mp4",
      });
      var videoURL = window.URL.createObjectURL(data);
      // DEVELOPMENT FEATURE
      const storageLink = await getAssignedUrl(
        userId,
        task.id,
        isChrome ? ".webm" : ".mp4"
      );
      try {
        await uploadFile(storageLink, blob);
        await uploadVideoMetadata(userId, task.id, isChrome ? ".webm" : ".mp4");
        await markTaskAsCompleted(userId, task.id);
        setLoading(false);
        setIsSubmitted(true);
        setTimeout(() => {
          setIndex(3);
        }, 1200);
      } catch (e) {
        // !TODO: Store video in localstorage and retry sending every 5 seconds.
        alert("There was an error uploading the recording. Please try again.");
        setLoading(false);
      }
    }
  };

  return (
    <Wrapper backgroundColor={"#FFF"}>
      {isModalVisible ? (
        <SubmitConfirmationModal
          loading={loading}
          setIsModalVisible={setIsModalVisible}
          isModalVisible={isModalVisible}
          isSubmitted={isSubmitted}
        />
      ) : null}
      <View
        style={{
          position: "absolute",
          top: 0,
          width: "100%",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          paddingVertical: 12,
          paddingHorizontal: 12,
          zIndex: 999999,
          shadowColor: "#000",
          shadowRadius: 20,
          shadowOpacity: 0.2,
        }}
      >
        <IonIcons
          name="arrow-back"
          size={20}
          color={"#000"}
          onPress={() => {
            if (capturing) {
              setIsModalVisible({
                title: "Are you sure?",
                subtitle: "You will lose the recording if you navigate back",
                callback: () => {
                  setIndex(4);
                },
                button_name: "Go Back",
              });
            } else {
              setIndex(5);
            }
          }}
        />
        <TranslateText style={{ fontSize: 20 }}>Video Journal</TranslateText>
        <TranslateText style={{ fontSize: 20 }}></TranslateText>
      </View>
      <View
        style={{
          flexDirection: "column",
          alignItems: "center",
          paddingTop: 40,
          paddingHorizontal: 40,
        }}
      >
        <TranslateText
          style={{
            fontWeight: "700",
            fontSize: 16,
            lineHeight: 19,
            color: "#006C48",
            marginVertical: 12,
            width: "100%",
            textTransform: "uppercase",
          }}
        >
          Quality Check
        </TranslateText>
        <View
          style={{
            marginVertical: 12,
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            padding: 0,
            overflow: "hidden",
          }}
        >
          {permissions.camera && permissions.microphone ? (
            <View
              style={{
                width: "100%",
                height: Dimensions.get("window").height * 0.6,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <View style={styles.overlayBox}>
                <View style={styles.borderCircle} />
              </View>
              <Webcam
                width={"100%"}
                height={"100%"}
                style={{ backgroundColor: "#FFF", objectFit: "cover" }}
                audio={true}
                muted={true}
                ref={webcamRef}
                mirrored={true}
                videoConstraints={videoConstraints}
              />
            </View>
          ) : (
            <View
              style={{
                width: 200,
                height: 200,
                borderRadius: 200,
                border: "1px solid #777777",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TranslateText style={{ textAlign: "center" }}>
                Camera is disabled
              </TranslateText>
            </View>
          )}
        </View>
        <TranslateText
          style={{
            fontWeight: "400",
            fontSize: 16,
            lineHeight: 19,
            marginVertical: 12,
          }}
        >
          Make sure your face is in the circle. Please remove any masks, do not
          cover your face or camera.
        </TranslateText>
      </View>
      <View
        style={{
          marginTop: 60,
          width: "100%",
          alignItems: "center",
        }}
      >
        <StartButton
          setQuestionVisible={setQuestionVisible}
          permissions={permissions}
          getPermissions={getPermissions}
        />
      </View>
      {questionVisible && (
        <RecordingRender
          capturing={capturing}
          startRecording={startRecording}
          task={task}
          setIsModalVisible={setIsModalVisible}
          stoppedRecording={stoppedRecording}
          setLoading={setLoading}
          submitRecording={stopRecording}
        />
      )}
    </Wrapper>
  );
};

const RecordingRender = ({
  task,
  capturing,
  startRecording,
  stoppedRecording,
  setIsModalVisible,
  setLoading,
  submitRecording,
}) => {
  return (
    <View
      style={{
        paddingHorizontal: 40,
        height: "100%",
        padding: 12,
        justifyContent: "center",
        position: "absolute",
        zIndex: 9999,
        backgroundColor: "#FFF",
        width: "100%",
      }}
    >
      <View
        style={{
          width: "100%",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <RecordingStatus
          capturing={capturing}
          stoppedRecording={stoppedRecording}
        />
      </View>
      <TranslateText
        style={{
          fontWeight: "700",
          fontSize: 16,
          lineHeight: 19,
          color: "#33CF71",
        }}
      >
        {capturing ? "GUIDING QUESTION" : "TAKE A SECOND TO READ THE QUESTION"}
      </TranslateText>
      <View style={{ marginBottom: 30, marginTop: 12 }}>
        <TranslateText
          style={{ fontWeight: "700", fontSize: 20, lineHeight: 19 }}
        >
          {task.title}
        </TranslateText>
        <TranslateText
          style={{
            fontWeight: "400",
            fontSize: 16,
            lineHeight: 19,
            paddingTop: 12,
          }}
        >
          {task.subtitle}
        </TranslateText>
        <View style={{ width: "40%", alignSelf: "center", marginTop: 20 }}>
          {!capturing && (
            <CustomButton title="Begin" onPress={startRecording} />
          )}
        </View>
      </View>
      <TranslateText
        style={{ fontWeight: "400", fontSize: 12, lineHeight: 19 }}
      >
        Please answer the question verbally in 3 minutes. You must answer for at
        least 20 seconds.
      </TranslateText>
      <ProgressBar capturing={capturing} stoppedRecording={stoppedRecording} />
      <View
        style={{
          flexDirection: "row",
          justifyContent: "flex-end",
          width: "100%",
        }}
      >
        <SubmitButton
          capturing={capturing}
          setIsModalVisible={setIsModalVisible}
          stoppedRecording={stoppedRecording}
          setLoading={setLoading}
          submitRecording={submitRecording}
        />
      </View>
    </View>
  );
};

const StartButton = ({ setQuestionVisible, permissions, getPermissions }) => {
  return permissions.camera && permissions.microphone ? (
    <TouchableOpacity
      onPress={() => {
        setQuestionVisible(true);
      }}
      style={{
        backgroundColor: "#33CF71",
        borderRadius: 20,
        marginTop: 8,
        padding: 12,
        width: "70%",
      }}
    >
      <View style={{ alignItems: "center" }}>
        <TranslateText
          style={{ color: "#FFF", fontSize: 16, fontWeight: "700" }}
        >
          START
        </TranslateText>
      </View>
    </TouchableOpacity>
  ) : (
    <TouchableOpacity
      onPress={() => {
        getPermissions();
      }}
      style={{
        backgroundColor: "#33CF71",
        borderRadius: 20,
        marginTop: 8,
        padding: 12,
        width: "70%",
      }}
    >
      <View style={{ alignItems: "center" }}>
        <TranslateText
          style={{ color: "#FFF", fontSize: 16, fontWeight: "700" }}
        >
          Enable Camera and Microphone
        </TranslateText>
      </View>
    </TouchableOpacity>
  );
};

const ProgressBar = ({ capturing, stoppedRecording }) => {
  const [counter, setCounter] = useState(0);

  useEffect(() => {
    if (!stoppedRecording && capturing) {
      setTimeout(() => {
        setCounter(counter + 0.2);
      }, 200);
    }
  }, [counter, capturing]);

  return (
    <View
      style={{
        width: "100%",
        height: 8,
        backgroundColor: "#C2C2C2",
        borderRadius: 20,
        marginVertical: 12,
        flexDirection: "row",
        alignItems: "center",
      }}
    >
      <View
        style={{
          width: `${
            (counter * 100) / 180 > 100 ? 100 : (counter * 100) / 180
          }%`,
          height: 8,
          backgroundColor: "#33CF71",
          borderRadius: 20,
        }}
      ></View>
    </View>
  );
};

const SubmitButton = ({
  capturing,
  setIsModalVisible,
  stoppedRecording,
  setLoading,
  submitRecording,
}) => {
  const [counter, setCounter] = useState(0);

  useEffect(() => {
    if (!stoppedRecording && capturing) {
      setTimeout(() => {
        setCounter(counter + 1);
      }, 1000);
    }
  }, [counter, capturing]);

  return (
    <TouchableOpacity
      onPress={() => {
        setIsModalVisible({
          title: "Are you sure?",
          subtitle:
            "Once you Submit the video you may not continue in the future.",
          callback: () => {
            setLoading(true);
            submitRecording();
          },
          button_name: "Submit",
        });
      }}
      disabled={counter < MIN_RECORD_TIME}
      style={{
        backgroundColor: "#FFF",
        borderRadius: 20,
        marginTop: 8,
        padding: 12,
        width: 80,
        marginHorizontal: 8,
      }}
    >
      <View style={{ alignItems: "center" }}>
        <TranslateText
          style={{
            color: counter < MIN_RECORD_TIME ? "#D9D9D9" : "#33CF71",
            fontSize: 14,
            fontWeight: "700",
          }}
        >
          Submit
        </TranslateText>
      </View>
    </TouchableOpacity>
  );
};

const RecordingStatus = ({ capturing, stoppedRecording }) => {
  if (capturing && !stoppedRecording) {
    return (
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <View
          style={{
            backgroundColor: "red",
            width: 10,
            height: 10,
            borderRadius: 10,
            marginRight: 8,
          }}
        />
        <TranslateText>Recording</TranslateText>...
      </View>
    );
  } else if (capturing && stoppedRecording) {
    return (
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <View
          style={{
            backgroundColor: "gray",
            width: 10,
            height: 10,
            borderRadius: 10,
            marginRight: 8,
          }}
        />
        <TranslateText>Stopped.</TranslateText>
      </View>
    );
  } else {
    return (
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <View
          style={{
            backgroundColor: "gray",
            width: 10,
            height: 10,
            borderRadius: 10,
            marginRight: 8,
          }}
        />
        <TranslateText>Ready to Record</TranslateText>
      </View>
    );
  }
};

const SubmitConfirmationModal = ({
  loading,
  setIsModalVisible,
  isModalVisible,
  isSubmitted,
}) => {
  return (
    <View
      style={{
        backgroundColor: "rgba(0,0,0,0.8)",
        height: "100%",
        width: "100%",
        position: "absolute",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 99999,
      }}
    >
      {isSubmitted ? (
        <View
          style={{
            backgroundColor: "#FBFDF8",
            height: "20%",
            width: "90%",
            zIndex: 99999,
            flexDirection: "column",
            justifyContent: "center",
            borderRadius: 8,
          }}
        >
          <View
            style={{
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <TranslateText
              style={{ fontSize: 16, fontWeight: 500, textAlign: "center" }}
            >
              Submission successful
            </TranslateText>
          </View>
        </View>
      ) : (
        <View
          style={{
            backgroundColor: "#FBFDF8",
            height: "20%",
            width: "90%",
            zIndex: 99999,
            flexDirection: "column",
            justifyContent: "space-between",
            borderRadius: 8,
          }}
        >
          <View style={{ paddingTop: 24, paddingLeft: 24 }}>
            <TranslateText style={{ fontSize: 16, fontWeight: 500 }}>
              {isModalVisible.title}
            </TranslateText>
            <TranslateText style={{ fontSize: 14 }}>
              {isModalVisible.subtitle}
            </TranslateText>
          </View>
          {loading ? (
            <View
              style={{
                flexDirection: "row",
                justifyContent: "flex-end",
                paddingBottom: 12,
                paddingRight: 12,
              }}
            >
              <ActivityIndicator />
            </View>
          ) : (
            <View
              style={{
                flexDirection: "row",
                justifyContent: "flex-end",
                paddingBottom: 12,
                paddingRight: 12,
              }}
            >
              <TouchableOpacity
                style={{
                  backgroundColor: "transparent",
                  padding: 12,
                  margin: 10,
                  borderRadius: 20,
                }}
                onPress={() => {
                  setIsModalVisible(null);
                }}
              >
                <TranslateText style={{ color: "#33CF71" }}>
                  Cancel
                </TranslateText>
              </TouchableOpacity>
              <TouchableOpacity
                style={{
                  backgroundColor: "#33CF71",
                  padding: 12,
                  margin: 10,
                  borderRadius: 20,
                }}
                onPress={() => {
                  isModalVisible.callback();
                }}
              >
                <TranslateText style={{ color: "#FFF" }}>
                  {isModalVisible.button_name}
                </TranslateText>
              </TouchableOpacity>
            </View>
          )}
        </View>
      )}
    </View>
  );
};

export default Task;

const styles = StyleSheet.create({
  bold: {
    fontWeight: "700",
  },
  overlayBox: {
    width: 680,
    height: 680,
    borderRadius: 1000,
    border: "150px solid rgba(255, 255, 255, 0.5)",
    position: "absolute",
    zIndex: 999999,
    backgroundColor: "transparent",
    alignItems: "center",
    justifyContent: "center",
  },
  borderCircle: {
    width: 380,
    height: 380,
    borderRadius: 200,
    border: "4px solid #0F0",
    zIndex: 999999,
  },
});
